import React from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Chip, IconButton, lighten, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useSelector } from 'store';
import { isHebrewString, safeParseJSON } from 'utils/helpers';
import { ChatUserLabel } from 'types/chat-users/chat-user-label';
import { phoneGlobalRegex } from 'utils/regexes';
import CopyToClipboard from 'react-copy-to-clipboard';
import { enqueueSnackbar } from 'notistack';
import { CopyAll, Label } from '@mui/icons-material';
import { ChatUsersCustomizationOption } from 'types/company-platforms/customizationSettings';
import { useIntl } from 'react-intl';

interface UserNameAndLabelProps {
  isRestricted24: boolean;
  labelJson: string | null;
  fullName: string;
  userDisplayPhoneNumber: string;
  platform: string;
  unreadMessagesNumber: number;
  selected?: boolean;
  hoverStyles: ChatUsersCustomizationOption | null;
  selectedStyles: ChatUsersCustomizationOption | null;
  unreadStyles: ChatUsersCustomizationOption | null;
  defaultStyles: ChatUsersCustomizationOption | null;
}

const UserNameAndLabel: React.FC<UserNameAndLabelProps> = ({
  isRestricted24,
  labelJson,
  fullName,
  userDisplayPhoneNumber,
  platform,
  unreadMessagesNumber,
  selected,
  hoverStyles,
  selectedStyles,
  unreadStyles,
  defaultStyles,
}) => {
  const showAvatar = useSelector((state) => state.userConfig.isAvatarActive);
  const userLabel: ChatUserLabel = safeParseJSON(labelJson) as ChatUserLabel;
  const matchesBigMobile = useMediaQuery((theme) => theme.breakpoints.down(768));
  const matchesMobile = useMediaQuery((theme) => theme.breakpoints.down(480));

  const intl = useIntl();

  return (
    <Grid container spacing={1} alignItems={'center'} flexWrap={'nowrap'}>
      {!showAvatar && (
        <Grid size="auto">
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              bgcolor: isRestricted24 ? 'success.main' : 'error.main',
            }}
          />
        </Grid>
      )}

      <Grid size="auto">
        <Box
          sx={{
            //TODO width: applicationType === 'extension' && showAvatar ? 80 : !userLabel ? 150 : 100,
            width: showAvatar ? (!userLabel ? 70 : 80) : !userLabel ? 150 : 90,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Tooltip
            title={
              <React.Fragment>
                <Box>{fullName}</Box>
                <Grid container spacing={1} alignItems={'center'}>
                  <Grid>
                    {phoneGlobalRegex.test(userDisplayPhoneNumber)
                      ? `0${userDisplayPhoneNumber.slice(3)}`
                      : userDisplayPhoneNumber || ''}
                  </Grid>
                  <Grid>
                    <CopyToClipboard
                      text={
                        userDisplayPhoneNumber
                          ? !platform
                            ? `0${userDisplayPhoneNumber.slice(3)}`
                            : userDisplayPhoneNumber
                          : ''
                      }
                      onCopy={() => {
                        enqueueSnackbar(
                          intl.formatMessage({
                            id: 'notifications.copied',
                          }),
                          {
                            variant: 'success',
                          },
                        );
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          color: 'currentColor',
                        }}
                      >
                        <CopyAll sx={{ width: 18, height: 18 }} />
                      </IconButton>
                    </CopyToClipboard>
                  </Grid>
                </Grid>
              </React.Fragment>
            }
            arrow
            placement="bottom-start"
          >
            <Typography
              //TODO variant={applicationType === 'extension' || matchesBigMobile ? 'body2' : 'body1'}
              variant={matchesBigMobile ? 'body2' : 'body1'}
              sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                // fontWeight: 700,
                direction: !isHebrewString(fullName) ? 'rtl' : 'ltr',
                fontWeight: unreadMessagesNumber > 0 ? 700 : 400,
                //TODO height: applicationType === 'extension' || matchesBigMobile ? 24 : 20,
                height: matchesBigMobile ? 24 : 20,

                '.MuiListItemButton-root:not(.Mui-selected):hover &': {
                  color:
                    hoverStyles?.value === 'custom'
                      ? hoverStyles.customColor.name + ' !important'
                      : // : 'rgba(0, 0, 0, 0.87) !important',
                        undefined,
                },
                color: selected
                  ? selectedStyles?.value === 'custom'
                    ? selectedStyles.customColor.name + ' !important'
                    : undefined
                  : unreadMessagesNumber > 0
                    ? unreadStyles?.value === 'custom'
                      ? unreadStyles.customColor.name + ' !important'
                      : undefined
                    : defaultStyles?.value === 'custom'
                      ? defaultStyles.customColor.name + ' !important'
                      : undefined,
                WebkitTransition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                transition: 'color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              }}
            >
              {fullName}
            </Typography>
          </Tooltip>
        </Box>
      </Grid>
      {userLabel && (
        <Grid size="grow" sx={{ minWidth: 0, maxWidth: 200 }}>
          {matchesMobile ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Label
                sx={{
                  color:
                    userLabel &&
                    userLabel.name !==
                      intl.formatMessage({
                        id: 'chat.header.filterLabel.noLabel',
                      }) &&
                    userLabel.name !== 'noLabel'
                      ? userLabel.color
                      : 'transparent',
                }}
              />
            </Box>
          ) : (
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
              {userLabel &&
                userLabel.name !==
                  intl.formatMessage({
                    id: 'chat.header.filterLabel.noLabel',
                  }) &&
                userLabel.name !== 'noLabel' && (
                  <Tooltip title={userLabel.name} arrow>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '100%',
                        borderRadius: 0.75,
                        px: 0.75,
                        bgcolor: userLabel.color && lighten(userLabel.color, 0.9),
                        border: userLabel.color && `1px solid ${userLabel.color}`,
                        direction: !isHebrewString(userLabel.name) ? 'rtl' : 'ltr',
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          direction: !isHebrewString(userLabel.name) ? 'rtl' : 'ltr',
                          fontSize: 10,
                          maxWidth: '100%',
                          color: userLabel.color || 'black',
                          fontWeight: 400,
                        }}
                      >
                        {userLabel.name}
                      </Box>
                    </Box>
                  </Tooltip>
                )}
            </Box>
          )}
        </Grid>
      )}
    </Grid>
  );
};

export default UserNameAndLabel;

